import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { SAVE_PRISMIC_DOCUMENTS } from '../redux/actions/prismic'
import { useTranslation } from 'react-i18next'
import Link from '../components/Link/Link'
import Image from 'next/image'
import clsx from 'clsx'
import {
  Box,
  Container,
  Grid,
  makeStyles,
  createStyles,
  Typography,
} from '@material-ui/core'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { useCalendly } from '../utils/useCalendly'
import { getTranslations } from '../i18n'
import { setFullLocale } from '../utils/utils'
import SharedHead from '../components/Global/SharedHead'
import Button from '../components/Button/Button'
import CMSText from '../components/Global/CMSText'
import { createClient, getSingleDocument } from '../prismicio'

const LOGO_HEIGHT = 80

const useStyles = makeStyles((theme) =>
  createStyles({
    header: {
      padding: theme.spacing(1),
    },
    logo: {
      height: LOGO_HEIGHT,
      position: 'relative',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: `calc(100vh - ${LOGO_HEIGHT * 2}px - ${theme.spacing(2)}px)`,
    },
  })
)

type Tprops = {
  parameters?: any
  stores?: any
  main: any
  inStoreText: any
}

const mapStateToProps = (state) => ({
  parameters: state.parameters,
  stores: state.stores,
})

const InStoreAppointmentsPage = ({
  parameters,
  stores,
  main,
  inStoreText,
}: Tprops) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const calendly = useCalendly(
    stores?.selectedStore,
    parameters?.items?.in_store
  )
  const { trackPageView, trackEvent } = useMatomo()

  useEffect(() => {
    dispatch(
      SAVE_PRISMIC_DOCUMENTS({
        general: main?.data,
        instore: inStoreText?.data,
      })
    )
  }, [main, inStoreText])

  useEffect(() => {
    trackPageView({ documentTitle: 'In Store appointment page' })
  }, [])

  return (
    calendly.ready &&
    calendly.returnUrl &&
    calendly.pickupUrl && (
      <div>
        <SharedHead datas={main} />
        <div className={clsx('rdv-header', classes.header)}>
          <div className={clsx('rdv-logo', classes.logo)}>
            <Image
              src={main.data.logo.url}
              layout="fill"
              objectFit="contain"
              objectPosition={'50% 50%'}
              alt=""
            />
          </div>
        </div>
        <Container>
          <div className={clsx('rdv-content', classes.content)}>
            <Typography variant={'h3'} component={'h1'} align="center">
              <CMSText
                defaultText={t('texts:instore:appointments_page_title')}
                data={inStoreText?.data?.appointments_page_title}
              />
            </Typography>

            <Grid container>
              <Grid item xs={6}>
                <Box pt={10} textAlign={'center'}>
                  <Link href="./rdv/pickup">
                    <a>
                      <Button
                        variant={'contained'}
                        color={'primary'}
                        component={'span'}
                        onClick={() =>
                          trackEvent({
                            category: 'In Store',
                            action: `User Go to In Store Pickup appointment page`,
                          })
                        }
                      >
                        <CMSText
                          defaultText={t(
                            'texts:instore:appointment_page_pickup_link_label'
                          )}
                          data={
                            inStoreText?.data
                              ?.appointment_page_pickup_link_label
                          }
                        />
                      </Button>
                    </a>
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box pt={10} textAlign={'center'}>
                  <Link href="./rdv/return">
                    <a>
                      <Button
                        variant={'contained'}
                        color={'primary'}
                        component={'span'}
                        onClick={() =>
                          trackEvent({
                            category: 'In Store',
                            action: `User Go to In Store Return appointment page`,
                          })
                        }
                      >
                        <CMSText
                          defaultText={t(
                            'texts:instore:appointment_page_return_link_label'
                          )}
                          data={
                            inStoreText?.data
                              ?.appointment_page_return_link_label
                          }
                        />
                      </Button>
                    </a>
                  </Link>
                </Box>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    )
  )
}

export async function getStaticProps({ locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const general = await getSingleDocument(client, lang, 'general')
  const inStoreText = await getSingleDocument(client, lang, 'instore')

  return {
    props: {
      main: general,
      inStoreText,
      i18nResources: await getTranslations(locale, ['texts']),
    },
    revalidate: 300,
  }
}

export default connect(mapStateToProps)(InStoreAppointmentsPage)
